// src/view/RegistrarSegPracticaAprendiz.jsx

import axios from "axios";
import Swal from "sweetalert2";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

/* =================== Permisos / Auth =================== */

const MODULO = "SeguimientoPractica";

const normalizarTexto = (texto = "") =>
  texto
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const leerJSONLocalStorage = (key, fallback = null) => {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch (error) {
    console.error(`Error leyendo ${key} desde localStorage:`, error);
    return fallback;
  }
};

const obtenerUsuarioStorage = () =>
  leerJSONLocalStorage("usuario", null) ||
  leerJSONLocalStorage("user", null) ||
  leerJSONLocalStorage("usuarios", null) ||
  {};

const obtenerModulosPermisos = () => {
  const permisosDirectos = leerJSONLocalStorage("permisos", []);
  const usuario = obtenerUsuarioStorage();

  if (Array.isArray(permisosDirectos) && permisosDirectos.length) {
    return permisosDirectos;
  }

  return (
    usuario?.modulos ||
    usuario?.permisos ||
    usuario?.rol?.modulos ||
    usuario?.rol?.permisos ||
    usuario?.usuario?.modulos ||
    usuario?.usuario?.permisos ||
    usuario?.user?.modulos ||
    usuario?.user?.permisos ||
    []
  );
};

const esSuperAdmin = () => {
  const usuario = obtenerUsuarioStorage();

  const rol =
    usuario?.rol?.nombreRol ||
    usuario?.rol?.nombre ||
    usuario?.rol ||
    usuario?.role ||
    usuario?.tipoUsuario ||
    usuario?.nombreRol ||
    usuario?.usuario?.rol ||
    usuario?.user?.rol ||
    "";

  const rolNormalizado = normalizarTexto(rol);

  return (
    rolNormalizado.includes("super_admin") ||
    rolNormalizado.includes("superadmin") ||
    rolNormalizado.includes("administrador")
  );
};

const obtenerPermisosModulo = (modulo) => {
  if (esSuperAdmin()) {
    return {
      modulo,
      LEER: true,
      CREAR: true,
      ACTUALIZAR: true,
      ELIMINAR: true,
      leer: true,
      crear: true,
      actualizar: true,
      eliminar: true,
    };
  }

  const modulos = obtenerModulosPermisos();

  return (
    modulos.find((permiso) => {
      const nombreModulo =
        permiso?.modulo ||
        permiso?.nombreModulo ||
        permiso?.moduloNombre ||
        permiso?.nombre ||
        permiso?.name ||
        "";

      return normalizarTexto(nombreModulo) === normalizarTexto(modulo);
    }) || {}
  );
};

const tienePermiso = (permisoModulo, accion) => {
  if (esSuperAdmin()) return true;
  if (!permisoModulo) return false;

  const accionMayuscula = accion.toUpperCase();
  const accionMinuscula = accion.toLowerCase();
  const accionNormalizada = normalizarTexto(accion);

  const alias = {
    leer: ["LEER", "leer", "VER", "ver", "READ", "read", "LISTAR", "listar"],
    crear: ["CREAR", "crear", "CREATE", "create", "ADD", "add"],
    actualizar: [
      "ACTUALIZAR",
      "actualizar",
      "EDITAR",
      "editar",
      "UPDATE",
      "update",
      "EDIT",
      "edit",
    ],
    eliminar: ["ELIMINAR", "eliminar", "DELETE", "delete", "BORRAR", "borrar"],
  };

  const accionesValidas =
    alias[accionNormalizada] || [accionMayuscula, accionMinuscula];

  const campoDirecto = accionesValidas.some(
    (campo) => permisoModulo?.[campo] === true
  );

  const campoPuede = accionesValidas.some((campo) => {
    const campoNormalizado = normalizarTexto(campo);
    const nombreCampo = `puede${campoNormalizado
      .charAt(0)
      .toUpperCase()}${campoNormalizado.slice(1)}`;

    return permisoModulo?.[nombreCampo] === true;
  });

  const enArreglos = [
    ...(Array.isArray(permisoModulo?.acciones) ? permisoModulo.acciones : []),
    ...(Array.isArray(permisoModulo?.permisos) ? permisoModulo.permisos : []),
    ...(Array.isArray(permisoModulo?.operaciones)
      ? permisoModulo.operaciones
      : []),
  ].some((item) =>
    accionesValidas.map(normalizarTexto).includes(normalizarTexto(item))
  );

  return campoDirecto || campoPuede || enArreglos;
};

const authConfig = () => {
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  return {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  };
};

/* =================== Constantes / Configuración =================== */

const ENUMS = {
  modalidadSesion: ["Presencial", "Virtual"],
  modalidadSeguimiento: ["Presencial", "Virtual", "A Distancia", "Híbrida"],
  formaEvaluacion: ["Virtual", "Presencial"],
  valoracion: ["Satisfactorio", "Por mejorar"],
  juicioEvaluacion: ["Aprobado", "No aprobado"],

  factoresTecnicos: [
    "Aplicación de conocimiento",
    "Mejora continua",
    "Fortalecimiento ocupacional",
    "Oportunidad y calidad",
    "Responsabilidad ambiental",
    "Administración de recursos",
    "Seguridad y salud en el trabajo",
    "Documentación etapa productiva",
  ],

  factoresActitudinales: [
    "Relaciones interpersonales",
    "Trabajo en equipo",
    "Solución de problemas",
    "Cumplimiento",
    "Organización",
  ],
};

const M1_LISTS = [
  {
    key: "comp",
    field: "m1_competenciasADesarrollar",
    title: "Competencias a desarrollar",
    placeholder: "Competencia…",
    icon: "fa-solid fa-layer-group",
    tone: "primary",
  },
  {
    key: "res",
    field: "m1_resultadosAprendizaje",
    title: "Resultados de aprendizaje",
    placeholder: "Resultado…",
    icon: "fa-solid fa-bullseye",
    tone: "success",
  },
  {
    key: "acts",
    field: "m1_actividadesADesarrollar",
    title: "Actividades a desarrollar",
    placeholder: "Actividad…",
    icon: "fa-solid fa-list-check",
    tone: "info",
  },
  {
    key: "evi",
    field: "m1_evidenciasAprendizaje",
    title: "Evidencias de aprendizaje",
    placeholder: "Evidencia…",
    icon: "fa-regular fa-file-lines",
    tone: "warning",
  },
  {
    key: "obs",
    field: "m1_observacionesAdicionales",
    title: "Observaciones adicionales",
    placeholder: "Observación…",
    icon: "fa-regular fa-comments",
    tone: "secondary",
    full: true,
  },
];

const FACTOR_BLOCKS = [
  {
    key: "m2Tec",
    field: "m2_factoresTecnicos",
    title: "Factores técnicos",
    modalTitle: "Momento 2 — Factores Técnicos",
    icon: "fa-solid fa-microscope",
    tone: "primary",
    variables: ENUMS.factoresTecnicos,
    moment: "m2",
  },
  {
    key: "m2Act",
    field: "m2_factoresActitudinales",
    title: "Factores actitudinales",
    modalTitle: "Momento 2 — Factores Actitudinales",
    icon: "fa-solid fa-users-gear",
    tone: "success",
    variables: ENUMS.factoresActitudinales,
    moment: "m2",
  },
  {
    key: "m3Tec",
    field: "m3_factoresTecnicos",
    title: "Factores técnicos",
    modalTitle: "Momento 3 — Factores Técnicos",
    icon: "fa-solid fa-microscope",
    tone: "primary",
    variables: ENUMS.factoresTecnicos,
    moment: "m3",
  },
  {
    key: "m3Act",
    field: "m3_factoresActitudinales",
    title: "Factores actitudinales",
    modalTitle: "Momento 3 — Factores Actitudinales",
    icon: "fa-solid fa-users-gear",
    tone: "success",
    variables: ENUMS.factoresActitudinales,
    moment: "m3",
  },
];

const FORM_SECTIONS = {
  m1: [
    {
      fields: [
        {
          name: "m1_horario",
          label: "Horario",
          placeholder: "Ejemplo: Lunes a viernes 8:00 a.m. - 5:00 p.m.",
          col: "col-12 col-md-6",
        },
        {
          name: "m1_enlaceGrabacion",
          label: "Enlace de grabación",
          type: "url",
          placeholder: "https://...",
          icon: "fa-solid fa-link",
          col: "col-12 col-md-6",
        },
      ],
    },
    {
      title: "Firmas",
      icon: "fa-regular fa-pen-to-square text-primary",
      fields: [
        {
          name: "m1_ciudad",
          label: "Ciudad",
          placeholder: "Ciudad",
          col: "col-md-4",
        },
        {
          name: "m1_fechaDiligenciamiento",
          label: "Fecha de diligenciamiento",
          type: "date",
          col: "col-md-4",
        },
        {
          name: "m1_modalidadSesion",
          label: "Modalidad de sesión",
          type: "select",
          options: ENUMS.modalidadSesion,
          col: "col-md-4",
        },
      ],
    },
  ],

  m2: [
    {
      fields: [
        {
          name: "m2_fechaMomentoSeguimiento",
          label: "Fecha del seguimiento",
          type: "date",
          col: "col-md-4",
        },
        {
          name: "m2_modalidadSeguimiento",
          label: "Modalidad",
          type: "select",
          options: ENUMS.modalidadSeguimiento,
          col: "col-md-4",
        },
        {
          name: "m2_enlaceGrabacion",
          label: "Enlace de grabación",
          type: "url",
          icon: "fa-solid fa-link",
          col: "col-md-4",
        },
      ],
    },
    {
      fields: [
        {
          name: "m2_obs_ComplementInst",
          label: "Complemento Instructor",
          type: "textarea",
          rows: 3,
          placeholder: "Observaciones del instructor",
          col: "col-md-4",
        },
        {
          name: "m2_obs_Aprendiz",
          label: "Aprendiz",
          type: "textarea",
          rows: 3,
          placeholder: "Observaciones del aprendiz",
          col: "col-md-4",
        },
        {
          name: "m2_obs_EnteCoformador",
          label: "Ente coformador",
          type: "textarea",
          rows: 3,
          placeholder: "Observaciones del ente",
          col: "col-md-4",
        },
      ],
    },
    {
      title: "Firmas",
      icon: "fa-regular fa-pen-to-square text-primary",
      fields: [
        {
          name: "m2_ciudad",
          label: "Ciudad",
          placeholder: "Ciudad",
          col: "col-md-4",
        },
        {
          name: "m2_fechaDiligenciamiento",
          label: "Fecha de diligenciamiento",
          type: "date",
          col: "col-md-4",
        },
        {
          name: "m2_modalidadSesion",
          label: "Modalidad de sesión",
          type: "select",
          options: ENUMS.modalidadSesion,
          col: "col-md-4",
        },
      ],
    },
  ],

  m3: [
    {
      fields: [
        {
          name: "m3_numeroVisitasRealizadas",
          label: "# Visitas realizadas",
          type: "number",
          min: 0,
          placeholder: "0",
          col: "col-md-4",
        },
        {
          name: "m3_formaEvaluacion",
          label: "Forma de evaluación",
          type: "select",
          options: ENUMS.formaEvaluacion,
          col: "col-md-4",
        },
        {
          name: "m3_enlaceGrabacion",
          label: "Enlace de grabación",
          type: "url",
          placeholder: "https://…",
          icon: "fa-solid fa-link",
          col: "col-md-4",
        },
      ],
    },
    {
      fields: [
        {
          name: "m3_retroEnte_proceso",
          label: "Ente — Proceso de formación",
          type: "textarea",
          rows: 3,
          placeholder: "Proceso de formación",
          col: "col-md-4",
        },
        {
          name: "m3_retroInstructor_proceso",
          label: "Instructor — Proceso de formación",
          type: "textarea",
          rows: 3,
          placeholder: "Proceso de formación",
          col: "col-md-4",
        },
        {
          name: "m3_retroAprendiz_proceso",
          label: "Aprendiz — Proceso de formación",
          type: "textarea",
          rows: 3,
          placeholder: "Proceso de formación",
          col: "col-md-4",
        },
        {
          name: "m3_retroEnte_desempeno",
          label: "Ente — Desempeño de competencias",
          type: "textarea",
          rows: 3,
          placeholder: "Desempeño de competencias",
          col: "col-md-4",
        },
        {
          name: "m3_retroInstructor_desempeno",
          label: "Instructor — Desempeño de competencias",
          type: "textarea",
          rows: 3,
          placeholder: "Desempeño de competencias",
          col: "col-md-4",
        },
        {
          name: "m3_retroAprendiz_desempeno",
          label: "Aprendiz — Desempeño de competencias",
          type: "textarea",
          rows: 3,
          placeholder: "Desempeño de competencias",
          col: "col-md-4",
        },
      ],
    },
    {
      fields: [
        {
          name: "m3_juicioEvaluacion",
          label: "Juicio de evaluación",
          type: "select",
          options: ENUMS.juicioEvaluacion,
          col: "col-md-6",
        },
      ],
    },
    {
      title: "Firmas",
      icon: "fa-regular fa-pen-to-square text-primary",
      fields: [
        {
          name: "m3_ciudad",
          label: "Ciudad",
          placeholder: "Ciudad",
          col: "col-md-4",
        },
        {
          name: "m3_fechaDiligenciamiento",
          label: "Fecha de diligenciamiento",
          type: "date",
          col: "col-md-4",
        },
        {
          name: "m3_modalidadSesion",
          label: "Modalidad de sesión",
          type: "select",
          options: ENUMS.modalidadSesion,
          col: "col-md-4",
        },
      ],
    },
  ],
};

const FIELD_CONFIGS = Object.values(FORM_SECTIONS).flatMap((sections) =>
  sections.flatMap((section) => section.fields)
);

const FIELD_NAMES = FIELD_CONFIGS.map((field) => field.name);

const FIELD_BY_NAME = FIELD_CONFIGS.reduce((acc, field) => {
  acc[field.name] = field;
  return acc;
}, {});

/* =================== Helpers de UI =================== */

const EmptyState = ({
  title = "Sin registros",
  subtitle = "Aún no has agregado información.",
}) => (
  <div className="text-center text-muted py-4">
    <div className="display-6 mb-2">🗂️</div>
    <div className="fw-semibold">{title}</div>
    <div className="small">{subtitle}</div>
  </div>
);

const Pill = ({ children, variant = "secondary" }) => (
  <span className={`badge rounded-pill text-bg-${variant}`}>{children}</span>
);

const IconBubble = ({ icon = "fa-regular fa-note-sticky", tone = "primary" }) => {
  const toneMap = {
    primary: "bg-primary-subtle text-primary",
    success: "bg-success-subtle text-success",
    info: "bg-info-subtle text-info",
    warning: "bg-warning-subtle text-warning",
    danger: "bg-danger-subtle text-danger",
    secondary: "bg-secondary-subtle text-secondary",
    dark: "bg-dark-subtle text-dark",
  };

  return (
    <div
      className={`rounded-circle d-flex align-items-center justify-content-center ${
        toneMap[tone] || toneMap.primary
      }`}
      style={{ width: 44, height: 44, minWidth: 44 }}
    >
      <i className={icon}></i>
    </div>
  );
};

const SectionCard = ({
  icon,
  tone = "primary",
  title,
  subtitle,
  children,
  action,
}) => (
  <div className="card border-0 shadow-sm h-100 section-card">
    <div className="card-body py-3">
      <div className="d-flex align-items-start gap-3">
        <IconBubble icon={icon} tone={tone} />

        <div className="w-100">
          <div className="d-flex justify-content-between align-items-center gap-2">
            <div className="fw-semibold">{title}</div>
            {action}
          </div>

          {subtitle && <div className="small text-muted mt-1">{subtitle}</div>}

          {children}
        </div>
      </div>
    </div>
  </div>
);

const FormActionButtons = ({
  formato,
  puedeCrear,
  puedeActualizar,
  puedeEliminar,
  saving,
  loading,
  onCancel,
  onDelete,
}) => {
  const puedeGuardar = (!formato && puedeCrear) || (formato && puedeActualizar);

  return (
    <div className="d-flex flex-wrap align-items-center gap-2">
      {formato && puedeEliminar && (
        <button
          className="btn btn-outline-danger"
          type="button"
          onClick={onDelete}
          disabled={saving}
        >
          <i className="fa-regular fa-trash-can me-1"></i>
          Eliminar
        </button>
      )}

      <button
        className="btn btn-ghost"
        type="button"
        onClick={onCancel}
        disabled={saving}
      >
        <i className="fa-regular fa-circle-xmark me-1"></i>
        Cancelar
      </button>

      {puedeGuardar && (
        <button className="btn btn-primary" type="submit" disabled={saving || loading}>
          <i className="fa-solid fa-floppy-disk me-1"></i>
          {formato ? "Actualizar" : "Guardar"}
        </button>
      )}
    </div>
  );
};

const FieldControl = ({ field, form }) => {
  const rawValue = form?.[field.name];
  const defaultValue = field.type === "date" ? toInputDate(rawValue) : rawValue ?? "";
  const baseClass = field.type === "select" ? "form-select" : "form-control";

  const commonProps = {
    name: field.name,
    className: baseClass,
    defaultValue,
  };

  if (field.type === "select") {
    return (
      <select {...commonProps}>
        <option value="">Seleccione…</option>
        {(field.options || []).map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }

  if (field.type === "textarea") {
    return (
      <textarea
        {...commonProps}
        rows={field.rows || 3}
        placeholder={field.placeholder || ""}
      />
    );
  }

  const input = (
    <input
      {...commonProps}
      type={field.type || "text"}
      min={field.min}
      placeholder={field.placeholder || ""}
    />
  );

  if (!field.icon) return input;

  return (
    <div className="input-group">
      <span className="input-group-text bg-white">
        <i className={field.icon}></i>
      </span>
      {input}
    </div>
  );
};

const FieldGrid = ({ sections = [], form }) => (
  <>
    {sections.map((section, sectionIndex) => (
      <div className={sectionIndex === 0 ? "row g-3" : "row g-3 mt-1"} key={section.title || sectionIndex}>
        {section.title && (
  <div className="col-12 mt-4 pt-2">
    <h6 className="fw-semibold mb-2">
      {section.icon && <i className={`${section.icon} me-2`}></i>}
      {section.title}
    </h6>
  </div>
)}

        {section.fields.map((field) => (
          <div className={field.col || "col-12"} key={field.name}>
            <label className="form-label small fw-semibold">{field.label}</label>
            <FieldControl field={field} form={form} />
          </div>
        ))}
      </div>
    ))}
  </>
);

/* =================== Modales =================== */

const ArrayEditorModal = ({
  show,
  onClose,
  title,
  placeholder,
  items = [],
  onAdd,
  onRemove,
  onEdit,
  saving = false,
}) => {
  const [value, setValue] = useState("");
  const [editIdx, setEditIdx] = useState(-1);
  const [editValue, setEditValue] = useState("");

  if (!show) return null;

  const limpiarModal = () => {
    setValue("");
    setEditIdx(-1);
    setEditValue("");
  };

  const handleClose = () => {
    limpiarModal();
    onClose?.();
  };

  const handleAdd = async () => {
    const text = String(value || "").trim();
    if (!text || saving) return;

    await onAdd?.(text);
    setValue("");
  };

  const startEdit = (idx, current) => {
    setEditIdx(idx);
    setEditValue(current);
  };

  const cancelEdit = () => {
    setEditIdx(-1);
    setEditValue("");
  };

  const saveEdit = async (idx) => {
    const newVal = String(editValue || "").trim();
    if (!newVal || saving) return;

    if (items[idx] === newVal) {
      cancelEdit();
      return;
    }

    await onEdit?.(idx, newVal);
    cancelEdit();
  };

  return (
    <>
      <div className="modal show" style={{ display: "block" }} role="dialog" aria-modal="true">
        <div className="modal-dialog modal-lg modal-dialog-centered" style={{ maxWidth: "min(960px, 95vw)" }}>
          <div className="modal-content shadow border-0 rounded-4">
            <div className="modal-header border-0 pb-0">
              <div>
                <h5 className="modal-title fw-bold">{title}</h5>
                <div className="small text-muted">
                  <i className="fa-regular fa-list-check me-1"></i>
                  Edición individual del seguimiento.
                </div>
              </div>

              <button type="button" className="btn-close" onClick={handleClose} disabled={saving} />
            </div>

            <div className="modal-body aem-body">
              {items.length === 0 ? (
                <EmptyState subtitle="Agrega el primer elemento con el campo inferior." />
              ) : (
                <div className="list-group rounded-3 border mb-3">
                  {items.map((item, index) => (
                    <div key={`${title}-${index}`} className="list-group-item">
                      <div className="aem-row">
                        <div className="aem-text">
                          {editIdx === index ? (
                            <textarea
                              className="form-control form-control-sm"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              rows={2}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && e.ctrlKey) {
                                  e.preventDefault();
                                  saveEdit(index);
                                }
                              }}
                              autoFocus
                            />
                          ) : (
                            <div style={{ wordBreak: "break-word", whiteSpace: "normal" }}>
                              {item}
                            </div>
                          )}
                        </div>

                        <div className="aem-actions">
                          {editIdx === index ? (
                            <>
                              <button
                                className="btn btn-sm btn-success"
                                type="button"
                                title="Guardar"
                                onClick={() => saveEdit(index)}
                                disabled={saving}
                              >
                                <i className="fa-solid fa-check" />
                              </button>

                              <button
                                className="btn btn-sm btn-outline-secondary"
                                type="button"
                                title="Cancelar"
                                onClick={cancelEdit}
                                disabled={saving}
                              >
                                <i className="fa-solid fa-rotate-left" />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                type="button"
                                title="Editar"
                                onClick={() => startEdit(index, item)}
                                disabled={saving}
                              >
                                <i className="fa-solid fa-pen" />
                              </button>

                              <button
                                className="btn btn-sm btn-outline-danger"
                                type="button"
                                onClick={() => onRemove?.(index)}
                                title="Eliminar"
                                disabled={saving}
                              >
                                <i className="fa-solid fa-trash" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-3">
                <label className="form-label small fw-semibold">Nuevo elemento</label>

                <div className="input-group">
                  <span className="input-group-text bg-white">
                    <i className="fa-regular fa-square-plus" />
                  </span>

                  <input
                    className="form-control"
                    placeholder={placeholder || "Texto…"}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    disabled={saving}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAdd();
                      }
                    }}
                  />

                  <button className="btn btn-success" type="button" onClick={handleAdd} disabled={saving}>
                    <i className="fa-solid fa-plus me-1" />
                    Añadir
                  </button>
                </div>

                <div className="form-text">Guarda el arreglo completo en el seguimiento individual.</div>
              </div>
            </div>

            <div className="modal-footer border-0 aem-footer d-flex justify-content-end gap-2">
              <button className="btn btn-secondary" type="button" onClick={handleClose} disabled={saving}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="modal-backdrop show" />
    </>
  );
};

const FactoresTableModal = ({
  show,
  onClose,
  title,
  variables = [],
  valoracionEnum = [],
  values = {},
  onSave,
  saving = false,
}) => {
  const [draftValues, setDraftValues] = useState({});

  useEffect(() => {
    if (show) {
      setDraftValues(values || {});
    }
  }, [show, values]);

  if (!show) return null;

  const handleChange = (variable, field, value) => {
    setDraftValues((prev) => ({
      ...prev,
      [variable]: {
        ...(prev?.[variable] || {}),
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    if (saving) return;

    const result = await onSave?.(draftValues);

    if (result !== null && result !== false) {
      onClose?.();
    }
  };

  return (
    <>
      <div className="modal show" style={{ display: "block" }} role="dialog" aria-modal="true">
        <div className="modal-dialog modal-xl modal-dialog-centered">
          <div className="modal-content shadow-lg border-0 rounded-4">
            <div className="modal-header border-0 pb-0">
              <div>
                <h5 className="modal-title mb-1">{title}</h5>
                <div className="small text-muted">
                  <i className="fa-regular fa-rectangle-list me-1"></i>
                  {variables.length} variables
                </div>
              </div>

              <button type="button" className="btn-close" onClick={onClose} disabled={saving} />
            </div>

            <div className="modal-body pt-3">
              {variables.length === 0 ? (
                <EmptyState subtitle="No hay variables definidas." />
              ) : (
                <div className="table-responsive rounded-3 border">
                  <table className="table table-sm table-striped align-middle mb-0">
                    <thead className="table-light sticky-top">
                      <tr className="text-muted">
                        <th>Variable</th>
                        <th style={{ width: 220 }}>Valoración</th>
                        <th>Observaciones</th>
                      </tr>
                    </thead>

                    <tbody>
                      {variables.map((variable) => (
                        <tr key={variable}>
                          <td className="fw-semibold">{variable}</td>

                          <td>
                            <select
                              className="form-select form-select-sm"
                              value={draftValues?.[variable]?.valoracion || ""}
                              disabled={saving}
                              onChange={(e) => handleChange(variable, "valoracion", e.target.value)}
                            >
                              <option value="">Seleccione…</option>
                              {valoracionEnum.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          </td>

                          <td>
                            <div className="input-group input-group-sm">
                              <span className="input-group-text bg-white">
                                <i className="fa-regular fa-comment-dots"></i>
                              </span>

                              <input
                                className="form-control"
                                placeholder="Observaciones"
                                value={draftValues?.[variable]?.observaciones || ""}
                                disabled={saving}
                                onChange={(e) => handleChange(variable, "observaciones", e.target.value)}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>

                    <tfoot className="table-light">
                      <tr>
                        <td colSpan={3} className="small text-muted py-2 px-3">
                          <i className="fa-regular fa-circle-question me-1"></i>
                          Ahora puedes escribir sin recargas. Usa el botón Guardar factores para actualizar el seguimiento.
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            <div className="modal-footer border-0 d-flex justify-content-end gap-2">
              <button className="btn btn-secondary" type="button" onClick={onClose} disabled={saving}>
                Cerrar
              </button>

              <button className="btn btn-primary" type="button" onClick={handleSave} disabled={saving}>
                <i className="fa-solid fa-floppy-disk me-1"></i>
                Guardar factores
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="modal-backdrop show"></div>
    </>
  );
};

/* =================== Utils de datos =================== */

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");

const safe = (v, fallback = "—") => (v || v === 0 ? v : fallback);

const fullName = (p1, p2, a1, a2) =>
  [p1, p2, a1, a2].filter(Boolean).join(" ").trim() || "—";

const toInputDate = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.slice(0, 10);

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
};

const toStringList = (list, key) => {
  if (!Array.isArray(list)) return [];

  return list
    .map((item) => {
      if (typeof item === "string") return item.trim();
      if (typeof item === "number") return String(item);

      if (item && typeof item === "object") {
        const value = item[key] ?? item.texto ?? item.nombre ?? item.descripcion ?? "";
        return String(value).trim();
      }

      return "";
    })
    .filter(Boolean);
};

const mapByVariable = (array = []) =>
  (Array.isArray(array) ? array : []).reduce((acc, item) => {
    if (item?.variable) {
      acc[item.variable] = {
        valoracion: item.valoracion || "",
        observaciones: item.observaciones || "",
      };
    }

    return acc;
  }, {});

const limpiarListaTexto = (items = []) =>
  (Array.isArray(items) ? items : [])
    .map((item) => String(item || "").trim())
    .filter(Boolean);

const mapValuesToFactorArray = (values = {}) =>
  Object.entries(values || {})
    .map(([nombreVariable, item]) => ({
      variable: nombreVariable,
      valoracion: item?.valoracion || "",
      observaciones: item?.observaciones || "",
    }))
    .filter((item) => item.valoracion || item.observaciones);

const normalizarSeguimientos = (data) => {
  if (Array.isArray(data?.seguimiento)) return data.seguimiento;
  if (Array.isArray(data?.seguimientos)) return data.seguimientos;
  if (data?.seguimiento) return [data.seguimiento];
  if (data?.formato) return [data.formato];
  return [];
};

const castValueForPatch = (name, value) => {
  const field = FIELD_BY_NAME[name];

  if (field?.type === "number") {
    return value === "" ? "" : Number(value);
  }

  return value;
};

/* =================== Página =================== */

const RegistrarSegPracticaAprendiz = () => {
  const permisosModulo = obtenerPermisosModulo(MODULO);

  const puedeLeer = tienePermiso(permisosModulo, "leer");
  const puedeCrear = tienePermiso(permisosModulo, "crear");
  const puedeActualizar = tienePermiso(permisosModulo, "actualizar");
  const puedeEliminar = tienePermiso(permisosModulo, "eliminar");

  const { etapaId, matriculaId, aprendizId } = useParams();
  const location = useLocation();
  const formRef = useRef(null);

  const [activeModal, setActiveModal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState({ etapa: null, aprendiz: null, matricula: null });
  const [formatos, setFormatos] = useState([]);

  const formato = formatos?.[0] || null;
  const form = formato || {};

  const formRenderKey = formato?._id
    ? `${formato._id}-${formato.updatedAt || formato.createdAt || ""}`
    : "seguimiento-nuevo";

  const api = useMemo(
    () => ({
      fetchByRelacion: (etapa, aprendiz, matricula) =>
        axios.get(
          `/api/seguimiento/listbyrelacion/${etapa}/${aprendiz}/${matricula}`,
          authConfig()
        ),
      createSeguimiento: (payload) =>
        axios.post("/api/seguimiento/add", payload, authConfig()),
      updateSeguimiento: (id, body) =>
        axios.put(`/api/seguimiento/update/${id}`, body, authConfig()),
      deleteSeguimiento: (id) =>
        axios.delete(`/api/seguimiento/delete/${id}`, authConfig()),
    }),
    []
  );

  const loadMeta = useCallback(async () => {
    if (!puedeLeer) {
      setLoading(false);
      Swal.fire({
        icon: "warning",
        title: "Sin permiso",
        text: "No tienes permiso para leer seguimiento de práctica.",
      });
      return;
    }

    if (!etapaId || !aprendizId || !matriculaId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const { data } = await api.fetchByRelacion(etapaId, aprendizId, matriculaId);

      setMeta({
        etapa: data?.etapa || null,
        aprendiz: data?.aprendiz || null,
        matricula: data?.matricula || null,
      });

      setFormatos(normalizarSeguimientos(data));
    } catch (error) {
      console.error("Error listByRelacion:", error);
      setMeta({ etapa: null, aprendiz: null, matricula: null });
      setFormatos([]);
    } finally {
      setLoading(false);
    }
  }, [api, aprendizId, etapaId, matriculaId, puedeLeer]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  const grupo = meta?.matricula?.grupoObjectId;
  const programa = grupo?.programaObjectId;

  const nombreAprendiz = useMemo(() => {
    const a = meta?.aprendiz;
    if (!a) return "—";

    return fullName(
      a?.nombre1Aprendiz,
      a?.nombre2Aprendiz,
      a?.apellido1Aprendiz,
      a?.apellido2Aprendiz
    );
  }, [meta?.aprendiz]);

  const nombreEnte =
    meta?.etapa?.nombreEnteconformador ??
    meta?.matricula?.enteObjectId?.nombreEnteconformador ??
    "—";

  const instructor = meta?.etapa?.instructorObjectId || null;
  const jefe = meta?.etapa?.jefeObjectId || null;

  const instructorNombre = instructor
    ? fullName(
        instructor?.nombre1Instructor,
        instructor?.nombre2Instructor,
        instructor?.apellido1Instructor,
        instructor?.apellido2Instructor
      )
    : "—";

  const jefeNombre = jefe
    ? fullName(
        jefe?.nombre1Jefe,
        jefe?.nombre2Jefe,
        jefe?.apellido1Jefe,
        jefe?.apellido2Jefe
      )
    : "—";

  const etapaFechaInicio =
    meta?.etapa?.fechaInicioEtapaPractica || meta?.etapa?.fechaInicio;
  const etapaFechaFin = meta?.etapa?.fechaFinEtapaPractica || meta?.etapa?.fechaFin;

  const qs = new URLSearchParams(location.search);
  const tituloOverride = (qs.get("titulo") || "").trim();
  const tituloPagina = tituloOverride.length ? tituloOverride : "Seguimiento Etapa Práctica";

  const listItems = useMemo(
    () =>
      M1_LISTS.reduce((acc, item) => {
        acc[item.key] = toStringList(form?.[item.field], "texto");
        return acc;
      }, {}),
    [form]
  );

  const factorValues = useMemo(
    () =>
      FACTOR_BLOCKS.reduce((acc, item) => {
        acc[item.key] = mapByVariable(form?.[item.field]);
        return acc;
      }, {}),
    [form]
  );

  const commitSeguimiento = async (patch = {}, options = {}) => {
    const { showSuccess = true, reload = true, allowEmptyPatch = false } = options;
    const isNew = !formato?._id;

    if (isNew && !puedeCrear) {
      Swal.fire({
        icon: "warning",
        title: "Sin permiso",
        text: "No tienes permiso para crear seguimiento de práctica.",
      });
      return null;
    }

    if (!isNew && !puedeActualizar) {
      Swal.fire({
        icon: "warning",
        title: "Sin permiso",
        text: "No tienes permiso para actualizar seguimiento de práctica.",
      });
      return null;
    }

    if (!etapaId || !aprendizId || !matriculaId) {
      Swal.fire({
        icon: "warning",
        title: "Faltan identificadores",
        text: "Verifica etapaId, aprendizId y matriculaId.",
      });
      return null;
    }

    if (!isNew && !Object.keys(patch).length && !allowEmptyPatch) {
      Swal.fire({
        icon: "info",
        title: "Sin cambios",
        text: "No se detectaron cambios para actualizar.",
        timer: 1500,
        showConfirmButton: false,
      });
      return null;
    }

    setSaving(true);

    try {
      const { data } = isNew
        ? await api.createSeguimiento({
            etapaPracticaObjectId: etapaId,
            aprendizObjectId: aprendizId,
            matriculaObjectId: matriculaId,
            ...patch,
          })
        : await api.updateSeguimiento(
            formato._id,
            Object.keys(patch).length ? patch : { _touch: true }
          );

      if (reload) await loadMeta();

      if (showSuccess) {
        Swal.fire({
          icon: "success",
          title: isNew ? "Seguimiento creado con éxito" : "Seguimiento actualizado con éxito",
          showConfirmButton: false,
          timer: 1500,
        });
      }

      return data?.seguimiento || data?.data || data;
    } catch (error) {
      console.error(isNew ? "Error creando seguimiento:" : "Error actualizando seguimiento:", error);

      Swal.fire({
        icon: "error",
        title: isNew ? "Error al guardar" : "Error al actualizar",
        text:
          error?.response?.data?.message ||
          (isNew ? "No se pudo crear el seguimiento." : "No se pudo actualizar el seguimiento."),
      });

      return null;
    } finally {
      setSaving(false);
    }
  };

  const buildPatchDesdeFormulario = () => {
    const fd = new FormData(formRef.current);
    const patch = {};

    FIELD_NAMES.forEach((name) => {
      const newVal = castValueForPatch(name, fd.get(name) ?? "");
      const oldVal = form?.[name] ?? "";

      if (String(newVal) !== String(oldVal)) {
        patch[name] = newVal;
      }
    });

    return patch;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    await commitSeguimiento(buildPatchDesdeFormulario());
  };

  const handleCancelar = () => {
    formRef.current?.reset();
  };

  const guardarListaTextoIndividual = async (path, nextItems = []) => {
    if (!path) return;

    await commitSeguimiento(
      { [path]: limpiarListaTexto(nextItems) },
      { showSuccess: false, reload: true }
    );
  };

  const addTextoArray = async (config, value) => {
    const text = String(value || "").trim();
    if (!text) return;

    const currentItems = listItems[config.key] || [];
    await guardarListaTextoIndividual(config.field, [...limpiarListaTexto(currentItems), text]);
  };

  const removeTextoArray = async (config, indexToRemove) => {
    const currentItems = listItems[config.key] || [];
    const nextItems = limpiarListaTexto(currentItems).filter((_, index) => index !== indexToRemove);

    await guardarListaTextoIndividual(config.field, nextItems);
  };

  const editTextoArray = async (config, indexToEdit, newValue) => {
    const text = String(newValue || "").trim();
    if (!text) return;

    const currentItems = listItems[config.key] || [];
    const nextItems = limpiarListaTexto(currentItems).map((item, index) =>
      index === indexToEdit ? text : item
    );

    await guardarListaTextoIndividual(config.field, nextItems);
  };

  const guardarFactoresIndividual = async (config, nextValues = {}) => {
    const nuevoArray = mapValuesToFactorArray(nextValues);

    return commitSeguimiento(
      { [config.field]: nuevoArray },
      { showSuccess: false, reload: true }
    );
  };

  const handleEliminar = async () => {
    if (!puedeEliminar) {
      Swal.fire({
        icon: "warning",
        title: "Sin permiso",
        text: "No tienes permiso para eliminar seguimiento de práctica.",
      });
      return;
    }

    if (!formato?._id) return;

    const result = await Swal.fire({
      icon: "warning",
      title: "Eliminar seguimiento",
      text: "¿Eliminar el formato SENA-032? Esta acción no se puede deshacer.",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      setSaving(true);
      await api.deleteSeguimiento(formato._id);
      await loadMeta();

      Swal.fire({
        icon: "success",
        title: "Formato eliminado",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error eliminando",
        text: error?.response?.data?.message || "Error eliminando.",
      });
    } finally {
      setSaving(false);
    }
  };

  const renderListCards = () => (
    <div className="row g-3 mt-1">
      {M1_LISTS.map((item) => {
        const items = listItems[item.key] || [];

        return (
          <div className={item.full ? "col-12" : "col-12 col-lg-6"} key={item.key}>
            <SectionCard
              icon={item.icon}
              tone={item.tone}
              title={item.title}
              subtitle="Ver y editar dentro del modal"
              action={
                <button
                  className="btn btn-sm btn-outline-secondary"
                  type="button"
                  onClick={() => setActiveModal(item.key)}
                >
                  <i className="fa-solid fa-table-list me-1"></i>
                  Abrir
                </button>
              }
            >
              <div className="mt-2 d-flex align-items-center justify-content-between">
                <div className="small text-muted">Elementos: {items.length}</div>
                <Pill variant="light">{items.length ? "Cargado" : "Por definir"}</Pill>
              </div>
            </SectionCard>
          </div>
        );
      })}
    </div>
  );

  const renderFactorCards = (moment) => (
    <div className="row g-3 mt-1">
      {FACTOR_BLOCKS.filter((item) => item.moment === moment).map((item) => {
        const values = factorValues[item.key] || {};

        return (
          <div className="col-12 col-lg-6" key={item.key}>
            <SectionCard
              icon={item.icon}
              tone={item.tone}
              title={item.title}
              subtitle="Ver y editar dentro del modal"
              action={
                <button
                  className="btn btn-sm btn-outline-secondary"
                  type="button"
                  onClick={() => setActiveModal(item.key)}
                >
                  <i className="fa-solid fa-table-list me-1"></i>
                  Abrir
                </button>
              }
            >
              <div className="mt-2 d-flex align-items-center justify-content-between">
                <div className="small text-muted">Variables: {item.variables.length}</div>
                <Pill variant="light">
                  {Object.keys(values).length ? "Cargado" : "Por definir"}
                </Pill>
              </div>
            </SectionCard>
          </div>
        );
      })}
    </div>
  );

  if (!puedeLeer) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning shadow-sm rounded-4">
          <h5 className="fw-bold mb-1">Sin permiso</h5>
          <div>No tienes permiso para consultar el módulo Seguimiento de Práctica.</div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} ref={formRef}>
      <style>{`
        .page-bg { background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%); min-height: 100%; }
        .section-card { border-radius: 1rem; box-shadow: 0 8px 24px rgba(16,24,40,.06); }
        .soft-separator { height: 1px; background: linear-gradient(90deg, transparent, rgba(0,0,0,.08), transparent); }
        .tabs-wrap .nav-link { border: none; border-bottom: 2px solid transparent; color: #64748b; font-weight: 600; padding: .75rem 1rem; border-radius: .5rem .5rem 0 0; }
        .tabs-wrap .nav-link:hover { color: #0ea5e9; }
        .tabs-wrap .nav-link.active { color: #0f172a; border-color: #0ea5e9; background: #ffffff; }
        .btn-ghost { background: #fff; border: 1px solid rgba(0,0,0,.08); }
        .aem-body { max-height: 65vh; overflow: auto; }
        .aem-row { display: grid; grid-template-columns: 1fr auto; align-items: start; gap: .75rem; }
        .aem-text { min-width: 0; word-break: break-word; white-space: normal; }
        .aem-actions { display: flex; gap: .5rem; align-items: center; flex-wrap: nowrap; }
        .aem-footer { position: sticky; bottom: 0; background: #fff; box-shadow: 0 -4px 16px rgba(16,24,40,.06); z-index: 1; }
      `}</style>

      {/* Encabezado */}
      <div className="d-flex flex-wrap justify-content-between align-items-end gap-3 mb-3">
        <div>
          <h1 className="fw-bold mb-1">
            <span className="me-2">
              <i className="fa-solid fa-file-pen text-primary"></i>
            </span>
            {tituloPagina}
          </h1>
        </div>

        <FormActionButtons
          formato={formato}
          puedeCrear={puedeCrear}
          puedeActualizar={puedeActualizar}
          puedeEliminar={puedeEliminar}
          saving={saving}
          loading={loading}
          onCancel={handleCancelar}
          onDelete={handleEliminar}
        />
      </div>

      {/* Tarjetas superiores */}
      {!loading ? (
        <div className="row g-3 mb-4">
          <div className="col-12 col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body py-3">
                <div className="d-flex align-items-start gap-3">
                  <div
                    className="rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center"
                    style={{ width: 42, height: 42 }}
                  >
                    <i className="fa-solid fa-user-graduate"></i>
                  </div>

                  <div className="w-100">
                    <div className="fw-semibold">{nombreAprendiz}</div>

                    <div className="small text-muted mt-1">
                      Documento: {safe(meta?.aprendiz?.documentoAprendiz)} · Tel:{" "}
                      {safe(meta?.aprendiz?.telefono1Aprendiz)} · Email: {" "}
                      {safe(meta?.aprendiz?.emailAprendiz)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body py-3">
                <div className="d-flex align-items-start gap-3">
                  <div
                    className="rounded-circle bg-success-subtle text-success d-flex align-items-center justify-content-center"
                    style={{ width: 42, height: 42 }}
                  >
                    <i className="fa-regular fa-calendar-check"></i>
                  </div>

                  <div>
                    <div className="small text-muted">Fecha de la Etapa Productiva</div>
                    <div className="fw-semibold">
                      {fmtDate(etapaFechaInicio)} — {fmtDate(etapaFechaFin)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6 col-lg-4">
            <SectionCard
              icon="fa-solid fa-book"
              tone="info"
              title="Programa de Formación"
              subtitle={safe(programa?.nombrePrograma)}
            />
          </div>

          <div className="col-12 col-md-6 col-lg-4">
            <SectionCard
              icon="fa-solid fa-people-group"
              tone="warning"
              title="Grupo"
              subtitle={safe(grupo?.codigoGrupo, meta?.matricula?.fichaGrupo ?? "—")}
            />
          </div>

          <div className="col-12 col-md-6 col-lg-4">
            <SectionCard
              icon="fa-solid fa-building"
              tone="secondary"
              title="Ente Conformador"
              subtitle={safe(nombreEnte)}
            />
          </div>

          <div className="col-12 col-md-6">
            <SectionCard
              icon="fa-solid fa-chalkboard-user"
              tone="primary"
              title="Instructor de Seguimiento"
              subtitle={safe(instructorNombre)}
            >
              <div className="small text-muted mt-2">
                Doc: {safe(instructor?.documentoInstructor)} · Tel: {" "}
                {safe(instructor?.telefono1Instructor || instructor?.telefonoInstructor)} · Email:{" "}
                {safe(instructor?.emailInstructor)}
              </div>
            </SectionCard>
          </div>

          <div className="col-12 col-md-6">
            <SectionCard
              icon="fa-solid fa-user-tie"
              tone="dark"
              title="Jefe Inmediato"
              subtitle={safe(jefeNombre)}
            >
              <div className="small text-muted mt-2">
                Doc: {safe(jefe?.documentoJefe)} · Tel: {" "}
                {safe(jefe?.telefono1Jefe || meta?.etapa?.enteObjectId?.jefeTelefono)} · Email:{" "}
                {safe(jefe?.emailJefe || meta?.etapa?.enteObjectId?.jefeCorreo)}
              </div>
            </SectionCard>
          </div>
        </div>
      ) : (
        <div className="row g-3 mb-4">
          <div className="col-12 col-lg-6">
            <SectionCard
              icon="fa-solid fa-user-graduate"
              tone="primary"
              title="Cargando aprendiz…"
              subtitle="—"
            />
          </div>

          <div className="col-12 col-lg-6">
            <SectionCard
              icon="fa-regular fa-calendar-check"
              tone="success"
              title="Cargando etapa…"
              subtitle="—"
            />
          </div>
        </div>
      )}

      {/* Tabs */}
      <div key={formRenderKey} className="card shadow-sm border-0 rounded-4">
        <div className="card-body p-0">
          <div className="tabs-wrap px-3 pt-3">
            <ul className="nav nav-tabs" role="tablist">
              <li className="nav-item">
                <button className="nav-link active" data-bs-toggle="tab" data-bs-target="#m1" type="button">
                  <i className="fa-regular fa-compass me-2"></i>
                  Momento 1 · Planeación
                </button>
              </li>

              <li className="nav-item">
                <button className="nav-link" data-bs-toggle="tab" data-bs-target="#m2" type="button">
                  <i className="fa-solid fa-chart-line me-2"></i>
                  Momento 2 · Seguimiento
                </button>
              </li>

              <li className="nav-item">
                <button className="nav-link" data-bs-toggle="tab" data-bs-target="#m3" type="button">
                  <i className="fa-solid fa-clipboard-check me-2"></i>
                  Momento 3 · Evaluación
                </button>
              </li>
            </ul>
          </div>

          <div className="soft-separator" />

          <div className="tab-content p-3 p-md-4">
            {/* ================= MOMENTO 1 ================= */}
            <div className="tab-pane fade show active" id="m1">
              <FieldGrid sections={[FORM_SECTIONS.m1[0]]} form={form} />
              {renderListCards()}
              <FieldGrid sections={[FORM_SECTIONS.m1[1]]} form={form} />
            </div>

            {/* ================= MOMENTO 2 ================= */}
            <div className="tab-pane fade" id="m2">
              <FieldGrid sections={[FORM_SECTIONS.m2[0]]} form={form} />
              {renderFactorCards("m2")}
              <FieldGrid sections={FORM_SECTIONS.m2.slice(1)} form={form} />
            </div>

            {/* ================= MOMENTO 3 ================= */}
            <div className="tab-pane fade" id="m3">
              <FieldGrid sections={[FORM_SECTIONS.m3[0]]} form={form} />
              {renderFactorCards("m3")}
              <FieldGrid sections={FORM_SECTIONS.m3.slice(1)} form={form} />
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="d-flex justify-content-end gap-2 mt-3">
        <FormActionButtons
          formato={formato}
          puedeCrear={puedeCrear}
          puedeActualizar={puedeActualizar}
          puedeEliminar={false}
          saving={saving}
          loading={loading}
          onCancel={handleCancelar}
          onDelete={handleEliminar}
        />
      </div>

      {/* =================== MODALES M1 =================== */}
      {M1_LISTS.map((item) => (
        <ArrayEditorModal
          key={item.key}
          show={activeModal === item.key}
          onClose={() => setActiveModal(null)}
          title={item.title}
          placeholder={item.placeholder}
          items={listItems[item.key] || []}
          saving={saving}
          onAdd={(value) => addTextoArray(item, value)}
          onRemove={(index) => removeTextoArray(item, index)}
          onEdit={(index, newValue) => editTextoArray(item, index, newValue)}
        />
      ))}

      {/* =================== MODALES M2 / M3 =================== */}
      {FACTOR_BLOCKS.map((item) => (
        <FactoresTableModal
          key={item.key}
          show={activeModal === item.key}
          onClose={() => setActiveModal(null)}
          title={item.modalTitle}
          variables={item.variables}
          valoracionEnum={ENUMS.valoracion}
          values={factorValues[item.key] || {}}
          saving={saving}
          onSave={(nextValues) => guardarFactoresIndividual(item, nextValues)}
        />
      ))}
    </form>
  );
};

export default RegistrarSegPracticaAprendiz;
